import { Routes } from '@angular/router';
import { Login } from './pages/login/login';
import { RegisterCompany } from './pages/register-company/register-company';
import { RegisterViaPortal } from './pages/register-via-portal/register-via-portal';
import { Home } from './pages/home/home';
import { AppShell } from './layout/app-shell/app-shell';
import { CompanyApprovalQueue } from './pages/company-approval-queue/company-approval-queue';
import { CompanyDetail } from './pages/company-detail/company-detail';
import { PlatformUsers } from './pages/platform-users/platform-users';
import { Projects } from './pages/projects/projects';
import { ProjectDetail } from './pages/project-detail/project-detail';
import { ApiKeys } from './pages/api-keys/api-keys';
import { RaiseExportRequest } from './pages/raise-export-request/raise-export-request';
import { ExportRequestsInbox } from './pages/export-requests-inbox/export-requests-inbox';
import { ExportRequestDetail } from './pages/export-request-detail/export-request-detail';
import { PlatformExportRequests } from './pages/platform-export-requests/platform-export-requests';
import { CompanyUsers } from './pages/company-users/company-users';
import { Wallet } from './pages/wallet/wallet';
import { PlatformWallet } from './pages/platform-wallet/platform-wallet';
import { CountryMappings } from './pages/country-mappings/country-mappings';
import { FieldRules } from './pages/field-rules/field-rules';
import { GeneralTerms } from './pages/general-terms/general-terms';
import { TermMappingMatrix } from './pages/term-mapping-matrix/term-mapping-matrix';
import { CountryTermCompare } from './pages/country-term-compare/country-term-compare';
import { AuditEvents } from './pages/audit-events/audit-events';
import { ZetrixStatus } from './pages/zetrix-status/zetrix-status';
import { MyCompany } from './pages/my-company/my-company';
import { CountrySchema } from './pages/country-schema/country-schema';
import { CountryXmlTemplates } from './pages/country-xml-templates/country-xml-templates';
import { XmlTemplateTester } from './pages/xml-template-tester/xml-template-tester';
import { AuthGuard } from './guards/auth-guard';
import { RoleGuard } from './guards/role-guard';

export const routes: Routes = [
  { path: 'login', component: Login },
  { path: 'register', component: RegisterCompany },
  { path: 'register-via-portal', component: RegisterViaPortal },
  {
    path: '',
    component: AppShell,
    canActivate: [AuthGuard],
    children: [
      { path: '', component: Home },
      {
        path: 'admin/companies',
        component: CompanyApprovalQueue,
        canActivate: [RoleGuard],
        data: { roles: ['SUPERADMIN', 'ADMIN'] },
      },
      {
        path: 'admin/companies/:id',
        component: CompanyDetail,
        canActivate: [RoleGuard],
        data: { roles: ['SUPERADMIN', 'ADMIN'] },
      },
      {
        path: 'admin/users',
        component: PlatformUsers,
        canActivate: [RoleGuard],
        data: { roles: ['SUPERADMIN'] },
      },
      {
        path: 'projects',
        component: Projects,
        canActivate: [RoleGuard],
        data: { roles: ['COMPANY_ADMIN', 'COMPANY_USER', 'SUPERADMIN', 'ADMIN', 'CUSTOMS', 'AUDITOR'] },
      },
      { path: 'projects/:id', component: ProjectDetail },
      {
        path: 'projects/:id/export-requests/new',
        component: RaiseExportRequest,
        canActivate: [RoleGuard],
        data: { roles: ['COMPANY_ADMIN', 'COMPANY_USER', 'SUPERADMIN'] },
      },
      {
        path: 'export-requests',
        component: ExportRequestsInbox,
        canActivate: [RoleGuard],
        data: { roles: ['COMPANY_ADMIN', 'COMPANY_USER'] },
      },
      {
        path: 'export-requests/:id',
        component: ExportRequestDetail,
        canActivate: [RoleGuard],
        data: { roles: ['COMPANY_ADMIN', 'COMPANY_USER', 'SUPERADMIN', 'ADMIN', 'CUSTOMS', 'AUDITOR'] },
      },
      {
        path: 'admin/export-requests',
        component: PlatformExportRequests,
        canActivate: [RoleGuard],
        data: { roles: ['SUPERADMIN', 'ADMIN', 'CUSTOMS', 'AUDITOR'] },
      },
      {
        path: 'api-keys',
        component: ApiKeys,
        canActivate: [RoleGuard],
        data: { roles: ['COMPANY_ADMIN'] },
      },
      {
        path: 'company-users',
        component: CompanyUsers,
        canActivate: [RoleGuard],
        data: { roles: ['COMPANY_ADMIN'] },
      },
      {
        path: 'company',
        component: MyCompany,
        canActivate: [RoleGuard],
        data: { roles: ['COMPANY_ADMIN', 'COMPANY_USER'] },
      },
      {
        path: 'wallet',
        component: Wallet,
        canActivate: [RoleGuard],
        data: { roles: ['COMPANY_ADMIN', 'COMPANY_USER'] },
      },
      {
        path: 'admin/wallet',
        component: PlatformWallet,
        canActivate: [RoleGuard],
        data: { roles: ['SUPERADMIN'] },
      },
      {
        path: 'admin/country-mappings',
        component: CountryMappings,
        canActivate: [RoleGuard],
        data: { roles: ['SUPERADMIN', 'ADMIN', 'AUDITOR'] },
      },
      {
        path: 'admin/field-rules',
        component: FieldRules,
        canActivate: [RoleGuard],
        data: { roles: ['SUPERADMIN', 'ADMIN', 'AUDITOR'] },
      },
      {
        path: 'admin/general-terms',
        component: GeneralTerms,
        canActivate: [RoleGuard],
        data: { roles: ['SUPERADMIN', 'ADMIN', 'AUDITOR'] },
      },
      {
        path: 'admin/term-mapping-matrix',
        component: TermMappingMatrix,
        canActivate: [RoleGuard],
        data: { roles: ['SUPERADMIN', 'ADMIN', 'AUDITOR'] },
      },
      {
        path: 'country-schema',
        component: CountrySchema,
        canActivate: [RoleGuard],
        data: { roles: ['SUPERADMIN', 'ADMIN', 'AUDITOR', 'COMPANY_ADMIN', 'COMPANY_USER'] },
      },
      {
        path: 'country-xml-templates',
        component: CountryXmlTemplates,
        canActivate: [RoleGuard],
        data: { roles: ['SUPERADMIN', 'ADMIN', 'AUDITOR', 'COMPANY_ADMIN', 'COMPANY_USER'] },
      },
      {
        path: 'xml-tester',
        component: XmlTemplateTester,
        canActivate: [RoleGuard],
        data: { roles: ['SUPERADMIN', 'ADMIN', 'AUDITOR', 'COMPANY_ADMIN', 'COMPANY_USER'] },
      },
      {
        path: 'country-compare',
        component: CountryTermCompare,
        canActivate: [RoleGuard],
        data: { roles: ['SUPERADMIN', 'ADMIN', 'AUDITOR', 'COMPANY_ADMIN', 'COMPANY_USER'] },
      },
      {
        path: 'admin/audit-events',
        component: AuditEvents,
        canActivate: [RoleGuard],
        data: { roles: ['SUPERADMIN', 'ADMIN', 'AUDITOR'] },
      },
      {
        path: 'admin/zetrix-status',
        component: ZetrixStatus,
        canActivate: [RoleGuard],
        data: { roles: ['SUPERADMIN', 'ADMIN', 'AUDITOR'] },
      },
    ],
  },
  { path: '**', redirectTo: '' },
];
